import { useState, useEffect } from 'react';
import { StaffSidebar } from '../../components/StaffSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';
import { CalendarCheck, Users, Clock, CheckCircle, XCircle, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffDashboard() {
  const { token } = useAuth();
  const { t, isRTL } = useLanguage();
  const [analytics, setAnalytics] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/staff/analytics`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }),
        axios.get(`${API}/staff/bookings?status=pending`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        })
      ]);
      setAnalytics(analyticsRes.data);
      setRecentBookings(bookingsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = analytics ? [
    { label: t('totalBookings'), value: analytics.total_bookings, icon: CalendarCheck, color: 'text-terracotta' },
    { label: t('pendingBookings'), value: analytics.pending_bookings, icon: Clock, color: 'text-golden-hour' },
    { label: t('confirmedBookings'), value: analytics.confirmed_bookings, icon: CheckCircle, color: 'text-sage' },
    { label: t('cancelledBookings'), value: analytics.cancelled_bookings, icon: XCircle, color: 'text-red-500' },
    { label: t('completedBookings'), value: analytics.completed_bookings, icon: TrendingUp, color: 'text-deep-charcoal' },
    { label: t('totalCustomers'), value: analytics.total_customers, icon: Users, color: 'text-terracotta' },
  ] : [];

  return (
    <div className={`min-h-screen bg-warm-sand ${isRTL ? 'rtl' : 'ltr'}`}>
      <StaffSidebar />
      
      <main className={`pt-16 lg:pt-0 ${isRTL ? 'lg:mr-64' : 'lg:ml-64'}`}>
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-deep-charcoal">
              {t('dashboard')}
            </h1>
            <p className="font-body text-deep-charcoal/60 mt-1">
              {t('overviewOperations')}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracotta"></div>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <Card 
                      key={index} 
                      className="bg-white rounded-xl border-0 shadow-soft"
                      data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <CardContent className="p-4">
                        <div className={`flex items-center justify-between mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Icon className={`w-5 h-5 ${stat.color}`} />
                        </div>
                        <p className="font-heading text-2xl font-bold text-deep-charcoal">
                          {stat.value}
                        </p>
                        <p className="font-body text-xs text-deep-charcoal/60">
                          {stat.label}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Charts Row */}
              <div className="grid lg:grid-cols-2 gap-6 mb-8">
                {/* Bookings by Category */}
                <Card className="bg-white rounded-xl border-0 shadow-soft">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg text-deep-charcoal">
                      {t('bookingsByCategory')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {analytics && Object.entries(analytics.bookings_by_category).map(([category, count]) => (
                        <div key={category} className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="font-body text-sm text-deep-charcoal capitalize">{category}</span>
                          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div className="w-32 h-2 bg-deep-charcoal/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-terracotta rounded-full"
                                style={{ width: `${(count / analytics.total_bookings) * 100}%` }}
                              />
                            </div>
                            <span className="font-body text-sm font-medium text-deep-charcoal w-8 text-right">
                              {count}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Bookings by Branch */}
                <Card className="bg-white rounded-xl border-0 shadow-soft">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg text-deep-charcoal">
                      {t('bookingsByBranch')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {analytics && Object.entries(analytics.bookings_by_branch).map(([branch, count]) => (
                        <div key={branch} className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="font-body text-sm text-deep-charcoal truncate max-w-[200px]">{branch}</span>
                          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div className="w-24 h-2 bg-deep-charcoal/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-sage rounded-full"
                                style={{ width: `${(count / analytics.total_bookings) * 100}%` }}
                              />
                            </div>
                            <span className="font-body text-sm font-medium text-deep-charcoal w-8 text-right">
                              {count}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Pending Bookings */}
              <Card className="bg-white rounded-xl border-0 shadow-soft">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-deep-charcoal">
                    {t('pendingBookingsTitle')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentBookings.length === 0 ? (
                    <p className="font-body text-deep-charcoal/60 text-center py-8">
                      {t('noPendingBookings')}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {recentBookings.map((booking) => (
                        <div 
                          key={booking.booking_id}
                          className={`flex items-center justify-between p-4 bg-warm-sand/50 rounded-xl ${isRTL ? 'flex-row-reverse' : ''}`}
                          data-testid={`pending-booking-${booking.booking_id}`}
                        >
                          <div>
                            <p className="font-body text-sm font-medium text-deep-charcoal">
                              {booking.customer_name}
                            </p>
                            <p className="font-body text-xs text-deep-charcoal/60">
                              {booking.service_name} • {booking.booking_date} at {booking.booking_time}
                            </p>
                          </div>
                          <div className={`${isRTL ? 'text-left' : 'text-right'}`}>
                            <p className="font-body text-xs text-deep-charcoal/50">
                              {booking.branch_name}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
